/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import {
  selectIsCumulative,
  selectTrendTimeVisuals,
  selectTrendTimeWeeklyView,
  selectIsFocusOnSet,
  selectActiveTrendTab,
} from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import {
  selectWeeklyTrendEndDate,
  selectWeeklyTrendStartDate,
  selectTrendTargetDate,
} from 'modules/dashboard/selectors';
import { isSameDay } from 'date-fns';
import { formatDate, getVisualName } from 'modules/dashboard/functions';
import { REPORT_TYPE } from 'modules/dashboard/constants';
import MuiTrendTimeTabularWidget from './mui-tabular-visual-view';
import MuiTrendHotelTabularWidget from './mui-tabular-hotel-visual-view';
import TrendFilter from '../../filter-panel';
import MuiTrendSegmentTabularWidget from './mui-tabular-segment-visual-view';
/**
 * Trend tab widget implementation
 * @param {Number} value - Trend time widget tab value
 * @param {Boolean} toggleDisabled - PMS sync toggle disabled state
 * @param {Date} latestDate - Latest booking date of selected hotel
 * @param {String} hotelTimezone - Timezone of selected hotel
 * @param {String} reportType - Report type of active tab
 * @returns
 */
const Widget = ({ value, latestDate, reportType, hotelTimezone, toggleDisabled }) => {
  // States are obtained from redux store. Refer each selector function for details.
  const visuals = useSelector(selectTrendTimeVisuals);
  const weeklyViewToggle = useSelector(selectTrendTimeWeeklyView);
  const startDate = useSelector(selectWeeklyTrendStartDate);
  const endDate = useSelector(selectWeeklyTrendEndDate);
  const targetDate = useSelector(selectTrendTargetDate);
  const isCumulative = useSelector(selectIsCumulative);
  const IsFocusOnSet = useSelector(selectIsFocusOnSet);
  const activeTab = useSelector(selectActiveTrendTab);
  //
  const [isLoaded, setIsLoaded] = useState(false);
  //
  const [label, setLabel] = useState(null);
  // Triggered when startDate, endDate, targetDate, weeklyViewToggle, isCumulative values change
  useEffect(() => {
    // Show adjusted stay dates for weekly view toggle on
    if (isCumulative) {
      setLabel(null);
    } else if (
      weeklyViewToggle &&
      (!isSameDay(new Date(startDate), new Date(targetDate.startDate)) ||
        !isSameDay(new Date(endDate), new Date(targetDate.endDate)))
    ) {
      setLabel(
        `Stay date selection adjusted.  New stay date range from: ${formatDate(
          startDate
        )} to ${formatDate(endDate)}`
      );
    } else {
      setLabel(null);
    }
  }, [startDate, endDate, targetDate, weeklyViewToggle, isCumulative]);
  //
  return (
    <>
      <Grid
        item
        xs={2}
        md={2}
        cc={2.5}
        sx={{
          ml: {
            xs: 0,
            cc: 0.25,
            md: 0,
          },
        }}
      >
        <TrendFilter
          reportType={reportType}
          visualName={getVisualName(visuals, 100)}
          tabValue={value}
          toggleDisabled={toggleDisabled}
          latestDate={latestDate}
          hotelTimezone={hotelTimezone}
          isLoaded={isLoaded}
        />
      </Grid>
      {IsFocusOnSet && (
        <Grid container item xs={10} cc={9.25} md={10} direction="column" mt={4}>
          <Grid item container justifyContent="start" px={12}>
            {label && <Typography paddingBottom={1}>{label}</Typography>}
          </Grid>
          {activeTab === REPORT_TYPE.TREND_TIME && (
            <MuiTrendTimeTabularWidget setIsTimeLoaded={setIsLoaded} isLoaded={isLoaded} />
          )}
          {activeTab === REPORT_TYPE.TREND_SEGMENT && (
            <MuiTrendSegmentTabularWidget setIsTimeLoaded={setIsLoaded} isLoaded={isLoaded} />
          )}
          {activeTab === REPORT_TYPE.TREND_HOTEL && (
            <MuiTrendHotelTabularWidget setIsTimeLoaded={setIsLoaded} isLoaded={isLoaded} />
          )}
        </Grid>
      )}
    </>
  );
};
//
export default Widget;
